import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProductDetailModal = ({ product, isOpen, onClose, onEdit, onDelete }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!isOpen || !product) return null;

  const productImages = product.images.length > 0 ? product.images : [product.image];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  const getStockStatusColor = (stock) => {
    if (stock === 0) return 'text-error';
    if (stock <= 10) return 'text-warning';
    return 'text-success';
  };

  const specifications = [
    { label: 'SKU', value: product.id },
    { label: 'Category', value: product.category.translations.en.name },
    { label: 'Brand', value: product.brand || 'N/A' },
    { label: 'Weight', value: product.weight ? `${product.weight} kg` : 'N/A' },
    { label: 'Dimensions', value: product.dimensions || 'N/A' },
    { label: 'Created', value: new Date(product.createdAt).toLocaleDateString() },
    { label: 'Last Updated', value: new Date(product.updatedAt).toLocaleDateString() }
  ];

  const inventoryHistory = [
    { date: '2025-01-20', action: 'Stock Added', quantity: '+50', reason: 'New shipment received' },
    { date: '2025-01-18', action: 'Stock Sold', quantity: '-15', reason: 'Order fulfillment' },
    { date: '2025-01-15', action: 'Stock Adjustment', quantity: '-2', reason: 'Damaged items removed' },
    { date: '2025-01-10', action: 'Stock Added', quantity: '+100', reason: 'Initial inventory' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-elevation-3 max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">{product.translations.en.name}</h2>
            <p className="text-sm text-muted-foreground mt-1">{product.translations.en.desc}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {/* Image Gallery */}
            <div className="space-y-4">
              <div className="relative bg-surface rounded-lg overflow-hidden aspect-square">
                <Image
                  src={productImages[currentImageIndex]}
                  alt={`${product.translations.en.name} - Image ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover"
                />
                
                {productImages.length > 1 && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={prevImage}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white"
                    >
                      <Icon name="ChevronLeft" size={20} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={nextImage}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white"
                    >
                      <Icon name="ChevronRight" size={20} />
                    </Button>
                  </>
                )}
                
                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2">
                  {productImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-micro ${
                        index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </div>
              
              {productImages.length > 1 && (
                <div className="flex space-x-2 overflow-x-auto">
                  {productImages.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-micro ${
                        index === currentImageIndex ? 'border-primary' : 'border-border'
                      }`}
                    >
                      <Image
                        src={image}
                        alt={`${product.translations.en.name} thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Details */}
            <div className="space-y-6">
              {/* Price and Stock */}
              <div className="bg-surface rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="text-2xl font-bold text-foreground">${product.price.toFixed(2)}</span>
                    {product.price_after_discount && product.price_after_discount > product.price && (
                      <span className="ml-2 text-lg text-muted-foreground line-through">
                        ${product.price_after_discount.toFixed(2)}
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-semibold ${getStockStatusColor(product.stock)}`}>
                      {product.stock} units
                    </div>
                    <div className="text-sm text-muted-foreground">in stock</div>
                  </div>
                </div>
                
                {product.stock <= 10 && product.stock > 0 && (
                  <div className="flex items-center space-x-2 text-warning">
                    <Icon name="AlertTriangle" size={16} />
                    <span className="text-sm">Low stock alert</span>
                  </div>
                )}
                
                {product.stock === 0 && (
                  <div className="flex items-center space-x-2 text-error">
                    <Icon name="AlertCircle" size={16} />
                    <span className="text-sm">Out of stock</span>
                  </div>
                )}
              </div>

              {/* Specifications */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">Specifications</h3>
                <div className="space-y-2">
                  {specifications.map((spec) => (
                    <div key={spec.label} className="flex justify-between py-2 border-b border-border">
                      <span className="text-muted-foreground">{spec.label}</span>
                      <span className="text-foreground font-medium">{spec.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Attributes */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">Attributes</h3>
                <div className="space-y-2">
                  {product.attributes.map((attribute) => (
                    <div key={attribute.id} className="flex justify-between py-2 border-b border-border">
                      <span className="text-muted-foreground">{attribute.translations.en.name}</span>
                      <span className="text-foreground font-medium">
                        {attribute.values.map(value => value.translations.en.value).join(', ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Inventory History */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">Recent Inventory Changes</h3>
                <div className="space-y-3 max-h-48 overflow-y-auto">
                  {inventoryHistory.map((entry, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-surface rounded-md">
                      <div className="flex-shrink-0 w-2 h-2 bg-primary rounded-full mt-2"></div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-foreground">{entry.action}</span>
                          <span className={`text-sm font-semibold ${
                            entry.quantity.startsWith('+') ? 'text-success' : 'text-error'
                          }`}>
                            {entry.quantity}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{entry.reason}</p>
                        <p className="text-xs text-muted-foreground">{entry.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-surface">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Close
          </Button>
          <Button
            variant="outline"
            onClick={() => onEdit(product)}
            iconName="Edit2"
            iconPosition="left"
          >
            Edit Product
          </Button>
          <Button
            variant="destructive"
            onClick={() => onDelete(product)}
            iconName="Trash2"
            iconPosition="left"
          >
            Delete Product
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailModal;
